/**
 * Enhanced Platform Service for Frontend
 * 
 * Unified service for all social media platform operations that integrates with
 * the backend Enhanced Platform Service. Follows SOLID principles with comprehensive
 * error handling, retry logic, and analytics tracking.
 * 
 * Features:
 * - Unified API for all platform operations
 * - Integration with backend enhanced platform service
 * - Comprehensive retry logic and circuit breaker protection
 * - Platform-specific validation and optimization
 * - Detailed analytics and event tracking
 * - Backward compatibility with existing PostService
 */

import { createRetryService } from './retryService';
import { ComposePostEvents } from './analytics/events';
import { getPlatformLimits, getPlatformDisplayName } from '../utils/platformUtils';
import { validateFile } from '../utils/fileValidation';
import { validateAltText } from '../utils/altTextValidation';

/**
 * Post data structure for unified platform operations
 */
export class PostData {
    constructor({
        content = '',
        platforms = [],
        socialAccountIds = [],
        mediaUrls = [],
        mediaTypes = [],
        altTexts = [],
        scheduledAt = null,
        bucketId = null,
        postType = 'text',
        threadData = null,
        platformSpecificData = {}
    }) {
        this.content = content;
        this.platforms = platforms;
        this.socialAccountIds = socialAccountIds;
        this.mediaUrls = mediaUrls;
        this.mediaTypes = mediaTypes;
        this.altTexts = altTexts;
        this.scheduledAt = scheduledAt;
        this.bucketId = bucketId;
        this.postType = postType; // 'text', 'image', 'video', 'carousel', 'thread'
        this.threadData = threadData;
        this.platformSpecificData = platformSpecificData; // TikTok settings, etc.
    }

    /**
     * Validate post data against platform requirements
     */
    validate() {
        const errors = [];
        const warnings = [];

        // Basic validation
        if (!this.content.trim() && this.mediaUrls.length === 0) {
            errors.push('Post must have either content or media');
        }

        if (this.platforms.length === 0) {
            errors.push('At least one platform must be selected');
        }

        if (this.platforms.length !== this.socialAccountIds.length) {
            errors.push('Platform and social account ID arrays must have same length');
        }

        // Platform-specific validation
        this.platforms.forEach((platform, index) => {
            const platformLimits = getPlatformLimits(platform);
            
            // Content length validation
            if (this.content.length > platformLimits.maxLength) {
                errors.push(`${getPlatformDisplayName(platform)} content exceeds ${platformLimits.maxLength} character limit`);
            }

            // Media requirements
            if (platformLimits.requiresMedia && this.mediaUrls.length === 0) {
                errors.push(`${getPlatformDisplayName(platform)} requires media`);
            }

            // Media limits
            if (this.mediaUrls.length > platformLimits.maxImages) {
                warnings.push(`${getPlatformDisplayName(platform)} supports max ${platformLimits.maxImages} images`);
            }

            // Alt text validation for accessibility
            if (this.mediaUrls.length > 0 && (!this.altTexts[index] || !this.altTexts[index].trim())) {
                warnings.push(`Missing alt text for ${getPlatformDisplayName(platform)} media`);
            }
        });

        // Thread validation
        if (this.postType === 'thread') {
            if (!this.threadData || this.threadData.length < 2) {
                errors.push('Thread must have at least 2 posts');
            }

            // Check thread-capable platforms
            const threadCapablePlatforms = ['twitter', 'bluesky'];
            const nonThreadPlatforms = this.platforms.filter(p => !threadCapablePlatforms.includes(p.toLowerCase()));
            if (nonThreadPlatforms.length > 0) {
                errors.push(`Thread posting not supported for: ${nonThreadPlatforms.join(', ')}`);
            }
        }

        return {
            isValid: errors.length === 0,
            errors,
            warnings
        };
    }

    /**
     * Get platform-specific optimized content
     */
    getOptimizedContent(platform) {
        const limits = getPlatformLimits(platform);
        
        if (this.content.length <= limits.maxLength) {
            return this.content;
        }

        // Truncate with ellipsis
        return this.content.substring(0, limits.maxLength - 3) + '...';
    }
}

/**
 * Result structure for platform operations
 */
export class PlatformOperationResult {
    constructor({
        success = false,
        platform = '',
        message = '',
        platformPostId = null,
        platformPostUrl = null,
        data = null,
        error = null,
        retryAttempts = 0,
        timestamp = new Date().toISOString()
    }) {
        this.success = success;
        this.platform = platform;
        this.message = message;
        this.platformPostId = platformPostId;
        this.platformPostUrl = platformPostUrl;
        this.data = data;
        this.error = error;
        this.retryAttempts = retryAttempts;
        this.timestamp = timestamp;
    }
}

/**
 * Multi-platform operation result
 */
export class MultiPlatformResult {
    constructor(results = {}) {
        this.results = results; // { platform: PlatformOperationResult }
        this.timestamp = new Date().toISOString();
    }

    get successful() {
        return Object.values(this.results).filter(r => r.success);
    }

    get failed() {
        return Object.values(this.results).filter(r => !r.success);
    }

    get overallSuccess() {
        return this.successful.length > 0;
    }

    get summary() {
        const total = Object.keys(this.results).length;
        const successCount = this.successful.length;
        return {
            total,
            successful: successCount,
            failed: total - successCount,
            platforms: {
                successful: this.successful.map(r => r.platform),
                failed: this.failed.map(r => r.platform)
            }
        };
    }
}

/**
 * Enhanced Platform Service - Main service class
 * Follows Single Responsibility Principle with clear separation of concerns
 */
export class EnhancedPlatformService {
    constructor(auth, trackingHandler, retryOptions = {}) {
        this.auth = auth;
        this.trackingHandler = trackingHandler;
        this.apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
        
        // Initialize retry service with enhanced configuration
        this.retryService = createRetryService(trackingHandler, {
            defaultMaxRetries: 3,
            defaultBaseDelay: 1000,
            defaultMaxDelay: 30000,
            enableCircuitBreaker: true,
            failureThreshold: 5,
            recoveryTimeout: 60000,
            ...retryOptions
        });

        // Platform capabilities cache
        this.platformCapabilities = null;
        this.capabilitiesExpiry = null;
    }

    /**
     * Create a unified post across multiple platforms
     * Main method that orchestrates multi-platform posting
     */
    async createPost(postData) {
        try {
            // Validate post data
            const validation = postData.validate();
            if (!validation.isValid) {
                throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
            }

            // Log warnings if any
            if (validation.warnings.length > 0) {
                console.warn('Post validation warnings:', validation.warnings);
            }

            // Track operation start
            this.trackingHandler.trackEvent(ComposePostEvents.POST_NOW_CLICKED.name, {
                platformCount: postData.platforms.length,
                postType: postData.postType,
                hasMedia: postData.mediaUrls.length > 0,
                hasBucket: !!postData.bucketId,
                isScheduled: !!postData.scheduledAt
            });

            // Handle different post types
            let results;
            if (postData.postType === 'thread') {
                results = await this._createThreadPost(postData);
            } else {
                results = await this._createUnifiedPost(postData);
            }

            // Track overall result
            const summary = results.summary;
            if (summary.successful > 0) {
                this.trackingHandler.trackEvent(ComposePostEvents.POST_SUCCESS.name, {
                    ...summary,
                    postType: postData.postType
                });
            }

            if (summary.failed > 0) {
                this.trackingHandler.trackEvent(ComposePostEvents.POST_FAILURE.name, {
                    ...summary,
                    failedPlatforms: summary.platforms.failed
                });
            }

            return results;

        } catch (error) {
            console.error('Enhanced platform service error:', error);
            this.trackingHandler.trackEvent(ComposePostEvents.POST_FAILURE.name, {
                error: error.message,
                platforms: postData.platforms
            });
            throw error;
        }
    }

    /**
     * Create unified post using backend enhanced platform service
     * @private
     */
    async _createUnifiedPost(postData) {
        const operation = postData.scheduledAt ? 'schedule_post' : 'create_post';
        
        return await this.retryService.executeWithRetry(async () => {
            // Prepare payload for backend enhanced platform service
            const payload = {
                post_data: {
                    content: postData.content,
                    post_type: postData.postType,
                    media_urls: postData.mediaUrls,
                    media_types: postData.mediaTypes,
                    alt_texts: postData.altTexts,
                    scheduled_at: postData.scheduledAt
                },
                platforms: postData.platforms.map((platform, index) => ({
                    platform: platform,
                    social_account_id: postData.socialAccountIds[index],
                    platform_specific_data: postData.platformSpecificData[platform] || {}
                })),
                bucket_id: postData.bucketId
            };

            // Call backend enhanced platform service
            const response = await fetch(`${this.apiBaseUrl}/social/enhanced-platform/create-post/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${this.auth.token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            
            // Transform backend response to frontend result format
            const results = {};
            data.results.forEach((result, index) => {
                const platform = postData.platforms[index];
                results[platform] = new PlatformOperationResult({
                    success: result.success,
                    platform: platform,
                    message: result.message,
                    platformPostId: result.platform_post_id,
                    platformPostUrl: result.platform_post_url,
                    data: result.data,
                    error: result.error,
                    retryAttempts: result.retry_attempts || 0
                });
            });

            return new MultiPlatformResult(results);

        }, {
            operation,
            maxRetries: 3,
            baseDelay: 2000
        });
    }

    /**
     * Create thread post using backend service
     * @private
     */
    async _createThreadPost(postData) {
        const operation = 'create_thread';
        
        return await this.retryService.executeWithRetry(async () => {
            const payload = {
                thread_data: postData.threadData,
                platforms: postData.platforms.map((platform, index) => ({
                    platform: platform,
                    social_account_id: postData.socialAccountIds[index]
                })),
                bucket_id: postData.bucketId,
                is_scheduled: !!postData.scheduledAt,
                scheduled_at: postData.scheduledAt
            };

            const response = await fetch(`${this.apiBaseUrl}/social/enhanced-platform/create-thread/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${this.auth.token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            
            // Transform response similar to unified post
            const results = {};
            data.results.forEach((result, index) => {
                const platform = postData.platforms[index];
                results[platform] = new PlatformOperationResult({
                    success: result.success,
                    platform: platform,
                    message: result.message,
                    platformPostId: result.platform_post_id,
                    platformPostUrl: result.platform_post_url,
                    data: result.data,
                    error: result.error,
                    retryAttempts: result.retry_attempts || 0
                });
            });

            return new MultiPlatformResult(results);

        }, {
            operation,
            maxRetries: 2,
            baseDelay: 3000
        });
    }

    /**
     * Get platform capabilities from backend
     */
    async getPlatformCapabilities(forceRefresh = false) {
        // Return cached capabilities if still valid
        if (!forceRefresh && this.platformCapabilities && 
            this.capabilitiesExpiry && Date.now() < this.capabilitiesExpiry) {
            return this.platformCapabilities;
        }

        try {
            const response = await fetch(`${this.apiBaseUrl}/social/enhanced-platform/capabilities/`, {
                headers: {
                    'Authorization': `Token ${this.auth.token}`
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch platform capabilities: ${response.statusText}`);
            }

            const data = await response.json();
            this.platformCapabilities = data.capabilities;
            this.capabilitiesExpiry = Date.now() + (5 * 60 * 1000); // Cache for 5 minutes

            return this.platformCapabilities;

        } catch (error) {
            console.error('Failed to fetch platform capabilities:', error);
            // Return cached data if available, otherwise default capabilities
            return this.platformCapabilities || this._getDefaultCapabilities();
        }
    }

    /**
     * Get default platform capabilities as fallback
     * @private
     */
    _getDefaultCapabilities() {
        return {
            twitter: { maxLength: 280, supportsImages: true, maxImages: 4 },
            facebook: { maxLength: 2000, supportsImages: true, maxImages: 10 },
            instagram: { maxLength: 2200, supportsImages: true, maxImages: 10, requiresMedia: true },
            linkedin: { maxLength: 3000, supportsImages: true, maxImages: 9 },
            tiktok: { maxLength: 2200, supportsImages: true, maxImages: 35 },
            bluesky: { maxLength: 300, supportsImages: true, maxImages: 4 },
            threads: { maxLength: 500, supportsImages: true, maxImages: 10 },
            mastodon: { maxLength: 500, supportsImages: true, maxImages: 4 }
        };
    }

    /**
     * Validate post before submission
     */
    async validatePost(postData) {
        try {
            const response = await fetch(`${this.apiBaseUrl}/social/enhanced-platform/validate/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${this.auth.token}`
                },
                body: JSON.stringify({
                    post_data: {
                        content: postData.content,
                        post_type: postData.postType,
                        media_urls: postData.mediaUrls,
                        media_types: postData.mediaTypes
                    },
                    platforms: postData.platforms
                })
            });

            if (!response.ok) {
                throw new Error(`Validation request failed: ${response.statusText}`);
            }

            return await response.json();

        } catch (error) {
            console.error('Post validation error:', error);
            // Fallback to frontend validation
            return postData.validate();
        }
    }

    /**
     * Get service health status
     */
    async getServiceHealth() {
        try {
            const response = await fetch(`${this.apiBaseUrl}/social/enhanced-platform/health/`, {
                headers: {
                    'Authorization': `Token ${this.auth.token}`
                }
            });

            if (!response.ok) {
                throw new Error(`Health check failed: ${response.statusText}`);
            }

            return await response.json();

        } catch (error) {
            console.error('Service health check error:', error);
            return {
                success: false,
                service_healthy: false,
                message: error.message
            };
        }
    }

    /**
     * Get service statistics
     */
    getStats() {
        return {
            retryStats: this.retryService.getStats(),
            circuitBreakerStats: this.retryService.getCircuitBreakerStats(),
            platformCapabilitiesCached: !!this.platformCapabilities,
            capabilitiesExpiry: this.capabilitiesExpiry
        };
    }

    /**
     * Reset circuit breakers for all operations
     */
    resetCircuitBreakers() {
        ['create_post', 'schedule_post', 'create_thread'].forEach(operation => {
            this.retryService.resetCircuitBreaker(operation);
        });
    }
}

/**
 * Factory function to create Enhanced Platform Service instance
 * Following the factory pattern for better dependency injection
 */
export const createEnhancedPlatformService = (auth, trackingHandler, retryOptions = {}) => {
    return new EnhancedPlatformService(auth, trackingHandler, retryOptions);
};

/**
 * Convenience function for creating posts with the enhanced service
 * Maintains backward compatibility while providing new capabilities
 */
export const createUnifiedPost = async (auth, trackingHandler, postConfig) => {
    const service = createEnhancedPlatformService(auth, trackingHandler);
    const postData = new PostData(postConfig);
    return await service.createPost(postData);
};

// Default export
export default EnhancedPlatformService; 